import React from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { 
  Button, 
  TextField, 
  Box, 
  Typography,
  Paper,
  InputLabel,
  FormHelperText
} from "@mui/material";
import { toast } from "react-toastify";
import * as Yup from "yup";
import useApiCaller from "hooks/apiCaller";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

const CreatePolicyForm = ({ onPolicyCreated }) => {
  const apiCaller = useApiCaller();

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Policy Name is required"),
    policyDocument: Yup.mixed().required("Policy Document is required"),
  });

  const handleSubmit = async (values) => {
    const formData = new FormData();
    formData.append("policyName", values.name);
    formData.append("companyId", "100"); 
    formData.append("policyDocument", values.policyDocument);

    try {
      const response = await apiCaller.post("/super-user/create-policy", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response) {
        toast.success("Policy Created Successfully!", { theme: "colored" });
        if (onPolicyCreated) {
          onPolicyCreated();
        }
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Failed to create policy.");
    }
  };

  return (
    <Paper 
      elevation={2}
      sx={{
        p: 3,
        borderRadius: '8px',
        maxWidth: '600px',
        margin: '0 auto',
      }}
    >
      <Typography 
        variant="h6" 
        sx={{ 
          mb: 3,
          color: '#1976d2',
          fontWeight: 500,
        }}
      >
        Upload New Policy
      </Typography>
      
      <Formik
        initialValues={{
          name: "",
          policyDocument: null,
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ setFieldValue, values }) => (
          <Form>
            {/* Policy Name */}
            <Box sx={{ mb: 3 }}>
              <InputLabel sx={{ mb: 1, color: '#333' }}>Policy Name</InputLabel>
              <Field
                as={TextField}
                name="name"
                placeholder="Enter policy name"
                fullWidth
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: '#1976d2',
                    },
                  },
                }}
              />
              <ErrorMessage name="name">
                {(msg) => <FormHelperText error>{msg}</FormHelperText>}
              </ErrorMessage>
            </Box>

            {/* Policy File Upload */}
            <Box sx={{ mb: 3 }}>
              <InputLabel sx={{ mb: 1, color: '#333' }}>Policy Document</InputLabel>
              <Box
                sx={{
                  border: '2px dashed #1976d2',
                  borderRadius: '8px',
                  p: 2,
                  textAlign: 'center',
                  cursor: 'pointer',
                  backgroundColor: 'rgba(25, 118, 210, 0.04)',
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.08)',
                  },
                }}
              >
                <input
                  type="file"
                  name="policyDocument"
                  accept=".doc,.docx,.pdf"
                  onChange={(event) =>
                    setFieldValue("policyDocument", event.currentTarget.files[0])
                  }
                  style={{ display: 'none' }}
                  id="policy-file-upload"
                />
                <label htmlFor="policy-file-upload">
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <CloudUploadIcon sx={{ fontSize: 40, color: '#1976d2', mb: 1 }} />
                    <Typography variant="body1" sx={{ color: '#1976d2' }}>
                      {values.policyDocument ? values.policyDocument.name : 'Click to upload or drag and drop'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#666', mt: 1 }}>
                      Supported formats: PDF, DOC, DOCX
                    </Typography>
                  </Box>
                </label>
              </Box>
              <ErrorMessage name="policyDocument">
                {(msg) => <FormHelperText error>{msg}</FormHelperText>}
              </ErrorMessage>
            </Box>

            {/* Submit Button */}
            <Button 
              type="submit" 
              variant="contained" 
              fullWidth 
              sx={{ 
                mt: 2,
                py: 1.5,
                backgroundColor: '#1976d2',
                '&:hover': {
                  backgroundColor: '#1565c0',
                },
              }}
            >
              Upload Policy
            </Button>
          </Form>
        )}
      </Formik>
    </Paper>
  );
};

export default CreatePolicyForm; 